import { useEffect, useState } from "react";
import { Cake, PartyPopper, Check, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { ShareImageDialog } from "@/components/BadgeShare";
import { renderBirthdayImage } from "@/lib/birthdayImage";
import { useAuth } from "@/context/AuthContext";

function BirthdayShare({ onClose }) {
  const { user } = useAuth();
  const [wishes, setWishes] = useState(null);
  useEffect(() => { api.get(`/users/${user.user_id}/birthday-wishes`).then(({ data }) => setWishes(data)).catch(() => setWishes([])); }, [user.user_id]);
  if (!wishes) return null;
  const link = `${window.location.origin}/registrieren?ref=${user.referral_code}`;
  return (
    <ShareImageDialog prefix="birthday" title="Geburtstags-Grußkarte teilen" alt="Happy Birthday" fileBase="itzcouple-geburtstag" onClose={onClose}
      description="Sag Danke für alle Glückwünsche – teile dein Grußbild in der Story oder per WhatsApp. Dein Einladungslink ist mit drauf."
      msg={`Danke für ${wishes.length} Geburtstags-Glückwünsche aus der itzPlay Community! 🎂💖 Mach mit: ${link}`}
      render={(format) => renderBirthdayImage({ user, wishes, link, format })} deps={[user, wishes, link]} />
  );
}

export function ShareBirthdayButton({ light }) {
  const [open, setOpen] = useState(false);
  return <>
    <button onClick={() => setOpen(true)} data-testid="birthday-share-open" className={`shrink-0 w-full sm:w-auto justify-center inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-xs font-bold active:scale-95 transition-transform ${light ? "bg-white text-rose-600" : "pink-gradient text-white"}`}><Share2 className="w-4 h-4" /> Grußkarte teilen</button>
    {open && <BirthdayShare onClose={() => setOpen(false)} />}
  </>;
}

function Person({ u, onWished }) {
  const [busy, setBusy] = useState(false);
  const wish = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/birthdays/${u.user_id}/congratulate`); onWished(u.user_id, data.wishes); toast.success(`Glückwunsch an ${u.name} gesendet 🎉`); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <li className="flex items-center gap-3" data-testid={`birthday-person-${u.user_id}`}>
      <UserLink id={u.user_id} testId={`birthday-avatar-${u.user_id}`}><UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-11 h-11" /></UserLink>
      <div className="flex-1 min-w-0">
        <UserLink id={u.user_id} testId={`birthday-name-${u.user_id}`} className="font-bold text-sm truncate block hover:underline">{u.name}</UserLink>
        <p className="text-xs text-slate-500" data-testid={`birthday-wishes-${u.user_id}`}>{u.wishes} {u.wishes === 1 ? "Glückwunsch" : "Glückwünsche"}</p>
      </div>
      {u.congratulated
        ? <span className="inline-flex items-center gap-1 px-3 h-9 rounded-full bg-emerald-50 text-emerald-600 text-xs font-bold" data-testid={`birthday-done-${u.user_id}`}><Check className="w-4 h-4" /> Gratuliert</span>
        : <button onClick={wish} disabled={busy} data-testid={`birthday-congratulate-${u.user_id}`} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full pink-gradient text-white text-xs font-bold active:scale-95 transition-transform disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />} Gratulieren
          </button>}
    </li>
  );
}

export function BirthdayCard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/birthdays/today").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d?.users.length) return null;
  const me = d.users.find((u) => u.is_me);
  const others = d.users.filter((u) => !u.is_me);
  const onWished = (id, wishes) => setD((s) => ({ ...s, users: s.users.map((u) => (u.user_id === id ? { ...u, congratulated: true, wishes } : u)) }));
  return (
    <section data-urgent="1" className="rounded-3xl p-5 border border-pink-100 bg-gradient-to-br from-white via-rose-50 to-fuchsia-50 shadow-sm" data-testid="birthday-card">
      {me && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-2xl pink-gradient text-white" data-testid="birthday-me-banner">
          <PartyPopper className="w-8 h-8 shrink-0 hidden sm:block" />
          <div className="flex-1 min-w-0"><p className="font-extrabold">Happy Birthday, {me.name}! 🎂</p><p className="text-sm opacity-90">+{d.bonus} Geschenk-Punkte von uns · {me.wishes} {me.wishes === 1 ? "Glückwunsch" : "Glückwünsche"} bisher</p></div>
          <ShareBirthdayButton light />
        </div>
      )}
      {others.length > 0 && <>
        <h2 className="font-bold text-lg flex items-center gap-2 mb-3"><Cake className="w-5 h-5 text-rose-500" /> Heute hat Geburtstag</h2>
        <ul className="space-y-3 max-h-80 overflow-y-auto" data-testid="birthday-list">{others.map((u) => <Person key={u.user_id} u={u} onWished={onWished} />)}</ul>
      </>}
    </section>
  );
}

export function BirthdayWishes({ userId, isMe }) {
  const [list, setList] = useState([]);
  const [all, setAll] = useState(false);
  useEffect(() => { api.get(`/users/${userId}/birthday-wishes`).then(({ data }) => setList(data)).catch(() => {}); }, [userId]);
  if (!list.length) return null;
  return (
    <section className="bg-white rounded-3xl p-5 border border-pink-100 shadow-sm" data-testid="profile-birthday-wishes">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="font-bold text-lg flex items-center gap-2"><Cake className="w-5 h-5 text-rose-500" /> {list.length} Geburtstags-{list.length === 1 ? "Glückwunsch" : "Glückwünsche"}</h2>
        {isMe && <ShareBirthdayButton />}
      </div>
      <div className="flex flex-wrap gap-2">
        {(all ? list : list.slice(0, 24)).map((w) => (
          <UserLink key={w.user_id} id={w.user_id} testId={`birthday-wish-from-${w.user_id}`} className="inline-flex items-center gap-2 pl-1 pr-3 h-9 rounded-full bg-rose-50 text-sm font-semibold text-rose-700 hover:bg-rose-100">
            <UserAvatar userId={w.user_id} name={w.name} picture={w.picture} size="w-7 h-7" text="text-xs" /> {w.name}
          </UserLink>
        ))}
        {!all && list.length > 24 && <button onClick={() => setAll(true)} data-testid="birthday-wishes-more" className="px-3 h-9 rounded-full bg-slate-100 text-sm font-semibold text-slate-600 hover:bg-slate-200">und {list.length - 24} weitere</button>}
      </div>
    </section>
  );
}
